<script setup>
import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { formatDayMonthDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useChips } from '@/composables/chips'
import { useCurrencyColumnFormatter } from '@/composables/data-grid'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ListItem from '@/components/shared/ListItem.vue'

defineProps({
  row: {
    type: Object,
    required: true,
  },
  currencyRef: {
    type: Number,
    default: null,
  },
})

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { costReportRowTypeEnum } = accountingStore
const { getStaffChips } = useChips()

const getStaffName = (staffId) => planningStore.staff.find(s => s.id === staffId)?.surname_name || '-'
const getTagName = (tagId) => accountingStore.costReportRowTagById.get(tagId)?.name || null
const getUomName = (uomId) => webSiteStore.getLocalizedUnitsOfMeasure().find(u => u.id === uomId)?.name || ''

const calcHours = (start, end) => {
  if (!start || !end) return null
  const diff = dayjs(`2000-01-01T${end}`).diff(dayjs(`2000-01-01T${start}`), 'minute') / 60
  return diff > 0 ? diff.toFixed(2) : null
}

const formatCostTotal = (row, currencyRef) => {
  const total = row.quantity != null && row.cost != null ? row.quantity * row.cost : null
  return total != null ? useCurrencyColumnFormatter({ total }, 'total', currencyRef) : ''
}

const getWorkedHoursLabel = (row) => {
  const h = calcHours(row.work_start, row.work_end)
  return h != null ? `${h} ${i18n.t('common.hoursAlt')}` : '-'
}

const getReportedHoursLabel = (row) => {
  const h = calcHours(row.reported_start, row.reported_end)
  return h != null ? `${h} ${i18n.t('common.hoursAlt')}` : '-'
}

const getItemLabel = (row) => {
  if (row.type === costReportRowTypeEnum.EXPENSE) return getTagName(row.tag) || row.notes || getStaffName(row.staff)
  return getStaffName(row.staff)
}
</script>

<template>
  <list-item
    v-if="row.type === costReportRowTypeEnum.TIME"
    style="padding: .75rem; min-width: initial;"
    class="row-card-border q-mt-sm q-ml-sm q-py-none"
    :hide-avatar="!$slots.avatar"
  >
    <template v-if="$slots.avatar" #avatar>
      <slot name="avatar" />
    </template>
    <template #label>
      <span style="font-weight: bolder;">{{ row.date ? formatDayMonthDate(row.date) : i18n.t('common.time') }}</span>
    </template>
    <template #caption>
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: .25rem .5rem;">
        <chip-template :values="getStaffChips(getStaffName(row.staff))" />
        <span v-if="row.travel_minutes">
          <q-icon name="mdi-car-outline" class="q-mr-xs" />{{ +(row.travel_minutes / 60).toFixed(2) }} {{ i18n.t('common.hoursAlt') }}
        </span>
        <span v-if="row.pause_minutes">
          <q-icon name="mdi-pause-circle-outline" class="q-mr-xs" />{{ +(row.pause_minutes / 60).toFixed(2) }} {{ i18n.t('common.hoursAlt') }}
        </span>
      </div>
    </template>
    <template #right>
      <q-item-section avatar class="align-middle align-center q-px-none" style="min-width: 0; margin-right: .25rem; align-items: flex-end;">
        <q-item-label style="font-weight: bold; white-space: nowrap;">
          <q-icon name="mdi-timer-outline" class="q-mr-xs" />{{ getReportedHoursLabel(row) }}
        </q-item-label>
        <q-item-label
          v-if="calcHours(row.work_start, row.work_end)"
          caption
          class="text-grey"
          style="white-space: nowrap;"
        >
          <q-icon name="mdi-briefcase-outline" class="q-mr-xs" />{{ getWorkedHoursLabel(row) }}
        </q-item-label>
      </q-item-section>
      <slot name="actions" />
    </template>
  </list-item>

  <list-item
    v-else
    style="padding: .3rem; min-width: initial;"
    class="row-card-border q-mt-sm q-ml-sm q-py-none"
    :hide-avatar="!$slots.avatar"
  >
    <template v-if="$slots.avatar" #avatar>
      <slot name="avatar" />
    </template>
    <template #label>
      <div style="display: flex; align-items: center;">
        <span style="font-weight: bolder;">{{ getItemLabel(row) }}</span>
        <span v-if="row.quantity != null" class="text-caption q-ml-xs">× {{ row.quantity }} {{ getUomName(row.unit_of_measure_ref) }}</span>
      </div>
    </template>
    <template #caption>
      <div style="display: flex; align-items: center;">
        <span v-if="row.date" class="caption">
          <q-icon name="mdi-calendar" class="q-mr-xs" />{{ formatDayMonthDate(row.date) }}
        </span>
        <chip-template :class="{ 'q-ml-xs': row.date }" :values="getStaffChips(getStaffName(row.staff))" />
      </div>
    </template>
    <template #right>
      <q-item-section avatar class="align-middle align-center q-px-none" style="min-width: 0; margin-right: .25rem; align-items: flex-end;">
        <q-item-label style="font-weight: bold; white-space: nowrap;">{{ formatCostTotal(row, currencyRef) }}</q-item-label>
      </q-item-section>
      <slot name="actions" />
    </template>
  </list-item>
</template>
